/** /tags/name-aliases — cross-tenant Tag_Name_Alias catalog (grid +
 *  create/edit dialog). An alias maps a tag NAME, or a name prefix, onto a
 *  canonical token inside one category, so a condition written once matches
 *  every spelling a tenant carries. A null tenant is a system-wide alias.
 *  Filters (operator scope / category / enabled) and search are held in local
 *  state and passed to the list query as `extra`. */
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import { Box, Button, Chip, MenuItem, TextField } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-pro';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';

import * as aliasesApi from '@/api/tagNameAliases';
import type { TagNameAlias } from '@/api/tagNameAliases';
import { deleteBatchAction } from '@/components/DataGrid/batchTypes';
import { copyableColumn } from '@/components/DataGrid/copyableColumn';
import {
  ResourceDataGrid,
  type ResourceDataGridParams,
} from '@/components/DataGrid/ResourceDataGrid';
import { ResponsiveFilterBar, type FilterBarItem } from '@/components/DataGrid/ResponsiveFilterBar';
import { PageHeader } from '@/components/PageHeader';
import { TenantLinkCell } from '@/components/TenantMark/TenantMark';
import {
  pickedSentinel,
  TenantPicker,
  type TenantPickerValue,
} from '@/components/TenantPicker/TenantPicker';
import { useCreateIntent } from '@/hooks/useCreateIntent';
import { usePermissions } from '@/hooks/usePermissions';
import { PAGE_FILL_SX } from '@/layout/pageFill';
import { formatRelativeTime } from '@/utils/format';
import { pageTenantsFrom } from '@/scope/pageTenants';
import { formatTenantIds } from '@/scope/tenantIds';
import { useScopedTenantsState } from '@/scope/useScopedTenantsState';

import { TagNameAliasFormDialog } from './TagNameAliasFormDialog';

/** Sentinel picker option: system-wide aliases (`tour_operator IS NULL`). */
const SYSTEM_TENANT: TenantPickerValue = { id: 0, name: 'System / global (no operator)' };
const SYSTEM_EXTRA_OPTIONS = [SYSTEM_TENANT];

export function TagNameAliasesListPage() {
  const { hasPerm } = usePermissions();
  const canManage = hasPerm('console_tag_aliases_manage');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);

  // The operators (several, D1) or the System sentinel alone (D3); they follow
  // the console scope until picked here.
  const [tenants, setTenants] = useScopedTenantsState();
  const [categoryFilter, setCategoryFilter] = useState('');
  const [enabledFilter, setEnabledFilter] = useState<'any' | 'true' | 'false'>('any');

  const tenantIds = formatTenantIds(tenants.map((t) => t.id));
  const tenantParam = pickedSentinel(tenants, SYSTEM_EXTRA_OPTIONS) ? 'system' : tenantIds;

  const anyFilterActive = tenants.length > 0 || categoryFilter !== '' || enabledFilter !== 'any';
  const resetAllFilters = () => {
    setTenants([]);
    setCategoryFilter('');
    setEnabledFilter('any');
  };

  const extra = useMemo(() => {
    const e: Record<string, string | number | boolean> = {};
    if (tenantParam) e.tour_operator_id = tenantParam;
    if (categoryFilter.trim()) e.category_code = categoryFilter.trim().toLowerCase();
    if (enabledFilter !== 'any') e.enabled = enabledFilter === 'true';
    return e;
  }, [tenantParam, categoryFilter, enabledFilter]);

  const editQuery = useQuery({
    queryKey: ['tag_name_aliases', 'detail', editId],
    queryFn: () => aliasesApi.retrieve(editId as number),
    enabled: editId !== null,
  });

  // The create dialog features the page's operators and pre-selects the one when
  // there is exactly one (D9); the System sentinel (id 0) names no operator.
  const pageTenants = pageTenantsFrom(tenantIds, tenants);

  const openCreate = () => {
    setEditId(null);
    setDialogOpen(true);
  };
  const openEdit = useCallback((id: number) => {
    setEditId(id);
    setDialogOpen(true);
  }, []);

  useCreateIntent(() => openCreate());

  const columns = useMemo<GridColDef<TagNameAlias>[]>(
    () => [
      copyableColumn({ field: 'id', headerName: 'ID', width: 80 }),
      {
        field: 'category_code',
        headerName: 'Category',
        width: 150,
      },
      {
        field: 'alias',
        headerName: 'Alias',
        flex: 1,
        minWidth: 220,
        renderCell: (p) =>
          canManage ? (
            <Box
              component="button"
              type="button"
              onClick={() => openEdit(p.row.id)}
              sx={{
                background: 'none',
                border: 'none',
                p: 0,
                cursor: 'pointer',
                color: '#0a1a3f',
                fontWeight: 500,
                font: 'inherit',
                textAlign: 'left',
              }}
            >
              {p.row.alias}
            </Box>
          ) : (
            p.row.alias
          ),
      },
      {
        field: 'match_mode',
        headerName: 'Match',
        width: 110,
        renderCell: (p) =>
          p.row.match_mode === 'prefix' ? (
            <Chip size="small" label="prefix" variant="outlined" />
          ) : (
            <Chip size="small" label="exact" variant="outlined" color="default" />
          ),
      },
      {
        field: 'canonical',
        headerName: 'Canonical',
        flex: 1,
        minWidth: 160,
        // The `~` is the spelling conditions use (reseller:~viator), so the
        // grid reads the same way the chips and template tags do.
        renderCell: (p) => <code>~{p.row.canonical}</code>,
      },
      {
        field: 'tenant',
        headerName: 'Operator',
        width: 170,
        valueGetter: (_, row) => row.tenant?.name ?? 'System',
        renderCell: (p) => <TenantLinkCell tenant={p.row.tenant} empty="System" />,
      },
      {
        // `enabled` is a filter-only param on the backend — it is NOT in the
        // list endpoint's ordering whitelist (_ORDER_FIELDS), so a header sort
        // would silently fall back to category_code. Keep it display-only.
        field: 'enabled',
        headerName: 'Enabled',
        width: 100,
        align: 'center',
        headerAlign: 'center',
        sortable: false,
        renderCell: (p) =>
          p.value ? <CheckIcon fontSize="small" color="success" aria-label="yes" /> : null,
      },
      {
        field: 'updated_ts',
        headerName: 'Updated',
        width: 140,
        sortable: true,
        renderCell: (p) => (
          <span title={p.row.updated_ts ?? ''}>{formatRelativeTime(p.row.updated_ts)}</span>
        ),
      },
    ],
    [canManage, openEdit],
  );

  const queryFn = useCallback(
    (params: ResourceDataGridParams) =>
      aliasesApi.list({
        page: params.page,
        pageSize: params.pageSize,
        ordering: params.ordering ?? 'category_code',
        search: params.search,
        ...(params.extra as Record<string, never>),
      }),
    [],
  );

  const toolbarItems: FilterBarItem[] = [
    {
      key: 'scope',
      priority: 5,
      active: tenants.length > 0,
      node: (
        <Box sx={{ minWidth: 280 }}>
          <TenantPicker
            multiple
            value={tenants}
            onChange={setTenants}
            label="Operator"
            extraOptions={SYSTEM_EXTRA_OPTIONS}
          />
        </Box>
      ),
    },
    {
      key: 'category',
      priority: 4,
      active: categoryFilter !== '',
      node: (
        <TextField
          size="small"
          label="Category"
          value={categoryFilter}
          sx={{ width: 180 }}
          placeholder="reseller"
          onChange={(e) => setCategoryFilter(e.target.value)}
        />
      ),
    },
    {
      key: 'enabled',
      priority: 3,
      active: enabledFilter !== 'any',
      node: (
        <TextField
          select
          size="small"
          label="Enabled"
          value={enabledFilter}
          sx={{ width: 160 }}
          onChange={(e) => setEnabledFilter(e.target.value as 'any' | 'true' | 'false')}
        >
          <MenuItem value="any">Any</MenuItem>
          <MenuItem value="true">Enabled</MenuItem>
          <MenuItem value="false">Disabled</MenuItem>
        </TextField>
      ),
    },
  ];

  return (
    <Box sx={PAGE_FILL_SX}>
      <PageHeader
        helpStory="tag-name-aliases"
        title="Tag Name Aliases"
        subtitle="Normalise the many spellings of one thing onto a canonical token, so a condition written once matches them all. A system alias has no owning operator; a tenant row shadows it."
        actions={
          canManage && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
              New tag name alias
            </Button>
          )
        }
      />

      <ResponsiveFilterBar items={toolbarItems} />

      <ResourceDataGrid<TagNameAlias>
        queryKey={['tag_name_aliases']}
        queryFn={queryFn}
        columns={columns}
        extra={extra}
        batchResource="tag_name_aliases"
        batchActions={[deleteBatchAction('console_tag_aliases_manage')]}
        defaultSort={{ field: 'category_code', sort: 'asc' }}
        emptyMessage="No tag name aliases yet."
        headerToolbar={{
          onResetAllFilters: resetAllFilters,
          resetDisabled: !anyFilterActive,
          search: true,
          searchPlaceholder: 'Alias, canonical, category…',
          exportFileName: 'tag-name-aliases',
        }}
      />

      {dialogOpen && (editId === null || editQuery.data) && (
        <TagNameAliasFormDialog
          open={dialogOpen}
          alias={editId === null ? null : (editQuery.data ?? null)}
          pageTenants={pageTenants}
          defaultCategory={categoryFilter.trim().toLowerCase()}
          onClose={() => {
            setDialogOpen(false);
            setEditId(null);
          }}
        />
      )}
    </Box>
  );
}
